<template>
    <ActionModal id="AcceptProfileVisibilityChangeModal" stretch @close="close">
        <template v-if="status !== 'done'" #image>
            <div></div>
        </template>

        <template #title>
            <template v-if="status !== 'done'">
                <strong
                    ><template v-if="isTypeMentor || isTypeVolunteer">Chceš</template
                    ><template v-else>Chcete</template> zmeniť viditeľnosť?</strong
                >
            </template>
            <template v-else>
                <template v-if="isTypeMentor">Viditeľnosť mentoringového profilu bola zmenená! </template>
                <template v-else-if="isTypeVolunteer">Viditeľnosť dobrovoľníckeho profilu bola zmenená! </template>
                <template v-else-if="isTypeUser">Viditeľnosť užívateľského profilu bola zmenená! </template>
                <template v-else>Viditeľnosť profilu bola zmenená!</template>
            </template>
        </template>

        <template #default>
            <template v-if="status !== 'done'">
                <template v-if="value === 'published'">
                    <template v-if="isTypeMentor">Mentorský profil bude odteraz viditeľný všetkým. </template>
                    <template v-else-if="isTypeVolunteer"
                        >Dobrovoľnícky profil bude odteraz viditeľný všetkým.
                    </template>
                    <template v-else-if="isTypeUser">Užívateľský profil bude odteraz viditeľný všetkým. </template>
                    <template v-else>Profil bude odteraz viditeľný všetkým. </template>
                </template>
                <template v-else-if="value === 'unlisted'">
                    <div>
                        <template v-if="isTypeMentor">Mentorský profil bude odteraz prístupný iba cez </template>
                        <template v-else-if="isTypeVolunteer"
                            >Dobrovoľnícky profil bude odteraz prístupný iba cez
                        </template>
                        <template v-else-if="isTypeUser">Užívateľský profil bude odteraz prístupný iba cez </template>
                        <template v-else>Profil bude odteraz prístupný iba cez </template>
                        <NuxtLink class="link" target="_blank" :to="profilePage">link </NuxtLink>
                        <br />
                        Nebude sa zobrazovať v zoznamoch, ani vo výsledkoch vyhľadávania.
                    </div>
                </template>
                <template v-else>
                    <template v-if="isTypeMentor">Mentorský profil bude odteraz viditeľný len Vám. </template>
                    <template v-else-if="isTypeVolunteer"
                        >Dobrovoľnícky profil bude odteraz viditeľný len Vám.
                    </template>
                    <template v-else-if="isTypeUser">Užívateľský profil bude odteraz viditeľný len Vám. </template>
                    <template v-else>Profil bude odteraz viditeľný len Vám. </template>
                </template>
            </template>
        </template>

        <template #footer>
            <CButton
                v-if="status !== 'done'"
                class="w-full uppercase sm:w-1/2"
                :disabled="status === 'loading'"
                :loading="status === 'loading'"
                size="large"
                variant="primary"
                @click="submit()"
            >
                Potvrdiť
            </CButton>
        </template>
    </ActionModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { PropType } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { EntityType } from '#shared/types/EntityType'
import type { UserId } from '#shared/types/ids'
import { LoadingState } from '#shared/types/LoadingState'
import { UserProfileType } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'

const props = defineProps({
    // can possibly change the profile visibility of other users
    profileId: {
        type: String as PropType<UserId>,
        required: true
    },
    slug: {
        type: String,
        required: true
    },
    type: {
        type: String as PropType<UserProfileType>,
        required: true
    },
    value: {
        type: String as PropType<VisibilityType>,
        required: true
    },
    onSubmit: {
        type: Function,
        required: true
    }
})

const emit = defineEmits(['close'])

const status = ref<LoadingState>('idle')

const isTypeMentor = computed((): boolean => {
    return props.type === EntityType.Mentor
})

const isTypeVolunteer = computed((): boolean => {
    return props.type === EntityType.Volunteer
})

const isTypeUser = computed((): boolean => {
    return props.type === EntityType.UserProfile
})

const profilePage = computed((): RouteLocationRaw => {
    if (isTypeMentor.value) {
        return {
            name: 'mentor-id',
            params: { id: props.slug || props.profileId }
        }
    } else if (isTypeVolunteer.value) {
        return {
            name: 'dobrovolnictvo-dobrovolnici-id',
            params: { id: props.slug || props.profileId }
        }
    } else if (isTypeUser.value) {
        return {
            name: 'u-id',
            params: { id: props.slug || props.profileId }
        }
    }
    return { name: 'index' }
})

async function submit() {
    status.value = 'loading'
    if (props.onSubmit) {
        const saved = await props.onSubmit()
        if (!saved) {
            close()
        }
        status.value = 'done'
    }
}

function close() {
    emit('close', true)
}
</script>